import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archivePartner,
  createPartner,
  createPartnerKey,
  getPartner,
  listPartnerKeys,
  listPartners,
  revokePartnerKey,
  updateKeyRecruitmentOrigin,
  updatePartner,
  type CreateKeyInput,
  type KeyRecruitmentSource,
  type CreatePartnerInput,
  type ListPartnerFilters,
  type UpdatePartnerInput,
} from '@/api/partners';

export function usePartners(filters: ListPartnerFilters = {}) {
  return useQuery({
    queryKey: ['partners', 'list', filters],
    queryFn: () => listPartners(filters),
    staleTime: 30_000,
  });
}

export function usePartner(id: number | undefined) {
  return useQuery({
    queryKey: ['partners', 'get', id],
    queryFn: () => getPartner(id as number),
    enabled: id != null,
  });
}

export function usePartnerKeys(partnerId: number | undefined) {
  return useQuery({
    queryKey: ['partners', 'keys', partnerId],
    queryFn: () => listPartnerKeys(partnerId as number),
    enabled: partnerId != null,
    staleTime: 15_000,
  });
}

export function useCreatePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePartnerInput) => createPartner(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partners', 'list'] }),
  });
}

export function useUpdatePartner(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePartnerInput) => updatePartner(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['partners', 'get', id] });
      void qc.invalidateQueries({ queryKey: ['partners', 'list'] });
    },
  });
}

export function useArchivePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => archivePartner(id),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['partners', 'get', id] });
      void qc.invalidateQueries({ queryKey: ['partners', 'list'] });
    },
  });
}

export function useCreatePartnerKey(partnerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateKeyInput) => createPartnerKey(partnerId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partners', 'keys', partnerId] }),
  });
}

export function useRevokePartnerKey(partnerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, reason }: { keyId: number; reason: string }) =>
      revokePartnerKey(partnerId, keyId, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partners', 'keys', partnerId] }),
  });
}

export function useUpdateKeyOrigin(partnerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      keyId: number;
      recruitmentSource: KeyRecruitmentSource;
      recruitmentPartner?: string;
    }) =>
      updateKeyRecruitmentOrigin(partnerId, input.keyId, {
        recruitmentSource: input.recruitmentSource,
        recruitmentPartner: input.recruitmentPartner,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partners', 'keys', partnerId] }),
  });
}
